import React, { useState, useEffect } from 'react';
import { ERROR_SEVERITY } from '../../utils/errorHandling';

/**
 * Global Error Toast System
 * Displays user-friendly error messages with appropriate styling
 */

let toastInstance = null;

export class ErrorToastManager {
  constructor() {
    this.toasts = [];
    this.listeners = new Set();
  }

  addToast(error, options = {}) {
    const toast = {
      id: Math.random().toString(36).substr(2, 9),
      error,
      timestamp: Date.now(),
      ...options
    };

    this.toasts.unshift(toast);
    
    // Limit to 5 toasts max
    if (this.toasts.length > 5) {
      this.toasts = this.toasts.slice(0, 5);
    }

    this.notifyListeners();

    // Auto-remove after delay based on severity
    const delay = this.getAutoRemoveDelay(error.severity);
    if (delay > 0) {
      setTimeout(() => {
        this.removeToast(toast.id);
      }, delay);
    }

    return toast.id;
  }

  removeToast(id) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
    this.notifyListeners();
  }

  clearAll() {
    this.toasts = [];
    this.notifyListeners();
  }

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback([...this.toasts]);
      } catch (error) {
        console.error('Error in toast listener:', error);
      }
    });
  }

  getAutoRemoveDelay(severity) {
    switch (severity) {
      case ERROR_SEVERITY.LOW: return 3000;
      case ERROR_SEVERITY.MEDIUM: return 5000;
      case ERROR_SEVERITY.HIGH: return 8000;
      case ERROR_SEVERITY.CRITICAL: return 0; // Manual dismiss only
      default: return 5000;
    }
  }
}

// Global instance
if (!toastInstance) {
  toastInstance = new ErrorToastManager();
}

export const errorToast = toastInstance;

/**
 * Error Toast Component
 * Displays individual error toast
 */
function ErrorToast({ toast, onDismiss }) {
  const { error } = toast;
  
  const getSeverityStyle = (severity) => {
    switch (severity) {
      case ERROR_SEVERITY.LOW:
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case ERROR_SEVERITY.MEDIUM:
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case ERROR_SEVERITY.HIGH:
        return 'bg-orange-50 border-orange-200 text-orange-800';
      case ERROR_SEVERITY.CRITICAL:
        return 'bg-red-50 border-red-200 text-red-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case ERROR_SEVERITY.LOW:
        return (
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case ERROR_SEVERITY.MEDIUM:
        return (
          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case ERROR_SEVERITY.HIGH:
      case ERROR_SEVERITY.CRITICAL:
        return (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <div className={`
      flex items-start p-4 rounded-lg border shadow-lg mb-3 animate-slide-in
      ${getSeverityStyle(error.severity)}
    `}>
      {/* Icon */}
      <div className="flex-shrink-0 mr-3 mt-0.5">
        {getSeverityIcon(error.severity)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">
          {error.userMessage}
        </p>
        
        {error.metadata?.operationName && (
          <p className="text-xs opacity-75 mt-1">
            Operation: {error.metadata.operationName}
          </p>
        )}

        {error.metadata?.queued && (
          <p className="text-xs opacity-75 mt-1">
            ⏳ Queued for retry when connection is restored
          </p>
        )}
      </div>

      {/* Dismiss Button */}
      <button
        onClick={onDismiss}
        className="flex-shrink-0 ml-3 text-current opacity-50 hover:opacity-75 transition-opacity"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

/**
 * Error Toast Container
 * Renders all active error toasts
 */
function ErrorToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    errorToast.addListener(setToasts);
    
    // Get initial toasts
    setToasts([...errorToast.toasts]);

    return () => {
      errorToast.removeListener(setToasts);
    };
  }, []);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 z-50 max-w-md">
      {toasts.map((toast) => (
        <ErrorToast
          key={toast.id}
          toast={toast}
          onDismiss={() => errorToast.removeToast(toast.id)}
        />
      ))}
    </div>
  );
}

/**
 * Helper function to show error toast
 * @param {Object} error - Standardized error object
 * @param {Object} options - Additional options
 */
export function showErrorToast(error, options = {}) {
  return errorToast.addToast(error, options);
}

/**
 * Helper function to show success toast
 * @param {string} message - Success message
 */
export function showSuccessToast(message) {
  return errorToast.addToast({
    userMessage: message,
    severity: ERROR_SEVERITY.LOW,
    type: 'success'
  });
}

export default ErrorToastContainer;
